import { forwardRef, type InputHTMLAttributes } from "react"

interface NumericInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  unit?: string
  hasError?: boolean
}

/**
 * Number input with optional unit suffix (e.g., "mg/L", "kg", "°C").
 * Forwards ref for React Hook Form register compatibility.
 */
export const NumericInput = forwardRef<HTMLInputElement, NumericInputProps>(
  ({ unit, hasError = false, className = "", ...props }, ref) => {
    const baseClasses =
      "w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 transition-colors"
    const stateClasses = hasError
      ? "border-red-300 focus:ring-red-500/20 focus:border-red-500"
      : "border-slate-200/50 focus:ring-emerald-500/20 focus:border-emerald-500"

    if (unit) {
      return (
        <div className="relative">
          <input
            ref={ref}
            type="number"
            step="any"
            className={`${baseClasses} ${stateClasses} pr-12 ${className}`}
            {...props}
          />
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <span className="text-sm text-gray-500">{unit}</span>
          </div>
        </div>
      )
    }

    return (
      <input
        ref={ref}
        type="number"
        step="any"
        className={`${baseClasses} ${stateClasses} ${className}`}
        {...props}
      />
    )
  }
)

NumericInput.displayName = "NumericInput"
